import { useEffect, useState } from "react";
import { Moon, Sun, Monitor } from "lucide-react";
import { useTheme } from "@/components/ThemeProvider";
import { cn } from "@/lib/utils";

type ThemeToggleProps = {
  /** `rail`: light icons on the dark sidebar; menu opens upward. */
  variant?: "default" | "rail";
};

export function ThemeToggle({ variant = "default" }: ThemeToggleProps) {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const triggerClass =
    variant === "rail"
      ? "rounded-md p-2 text-slate-300 transition-colors hover:bg-slate-800/80 hover:text-white"
      : "rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10";

  if (!mounted) {
    return (
      <button type="button" className={triggerClass} aria-label="Theme">
        <Sun className="size-5" />
      </button>
    );
  }

  const current = resolvedTheme;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={triggerClass}
        aria-label="Toggle theme"
      >
        {current === "dark" ? <Moon className="size-5" /> : <Sun className="size-5" />}
      </button>
      {open ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40"
            aria-label="Close theme menu"
            onClick={() => setOpen(false)}
          />
          <div
            className={cn(
              "absolute right-0 z-50 w-36 rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-white/10 dark:bg-gray-900",
              variant === "rail" ? "bottom-full mb-1" : "top-full mt-1",
            )}
          >
            {(
              [
                ["light", Sun, "Light"],
                ["dark", Moon, "Dark"],
                ["system", Monitor, "Auto"],
              ] as const
            ).map(([value, Icon, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  setTheme(value);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 ${
                  theme === value ? "bg-gray-100 dark:bg-gray-800" : "hover:bg-gray-50 dark:hover:bg-white/5"
                }`}
              >
                <Icon className="size-4 shrink-0" />
                {label}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
